/* This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at http://mozilla.org/MPL/2.0/. */

import { AuthUiErrors } from './auth-errors/auth-errors';
import { base64urlToBytes } from './base64url';
import {
  JwtTokenCache,
  MfaOtpRequestCache,
  sessionToken as getSessionToken,
} from './cache';
import { MfaScope } from './types';

/**
 * Clears the MFA and JWT cache for the given scope if the error is an invalid JWT.
 *
 * Use this when checking the response from the auth server for an invalid JWT.
 * @param e - The error to check, must have code and errno properties.
 * @param scope - The scope to clear from the MFA and JWT cache.
 * @param sessionTokenOverride - Session token whose cache entries should be
 *   cleared. Defaults to the globally-cached session token. Pass this in hosts
 *   (e.g. the Signin inline flows) whose guard is keyed on an injected session
 *   token rather than the global one.
 * @returns true if the cache was cleared, false otherwise.
 */
export const clearMfaAndJwtCacheOnInvalidJwt = (
  e: any,
  scope: MfaScope,
  sessionTokenOverride?: string
): boolean => {
  const sessionToken = sessionTokenOverride ?? getSessionToken();
  if (!sessionToken) {
    // noop - we can't do anything without a session token
    return false;
  }
  const shouldClear = isInvalidJwtError(e);
  if (shouldClear) {
    MfaOtpRequestCache.remove(sessionToken, scope);
    JwtTokenCache.removeToken(sessionToken, scope);
  }
  return shouldClear;
};

/**
 * Checks if the error is an invalid JWT error.
 * @param e - The error to check, must have code and errno properties.
 * @returns
 */
export const isInvalidJwtError = (e: unknown): boolean => {
  const errno = (e as any)?.errno;
  return (
    // 223 - the auth server's dedicated invalid/expired MFA token error.
    errno === AuthUiErrors.INVALID_MFA_TOKEN.errno ||
    // 110 - a generic invalid token. The MFA strategy resolves a JWT's parent
    // session (stid) via db.sessionToken, which throws 110 when that session is
    // gone (e.g. after a password change). Treat it as a stale-JWT signal too.
    errno === AuthUiErrors.INVALID_TOKEN.errno
  );
};

/**
 * The account an MFA token names. The server files scope-bound records under
 * this `sub`, so anything that binds data to the proof has to agree on it.
 *
 * @param mfaToken - The MFA JWT to read.
 * @returns The uid, or undefined if the token is malformed.
 */
export const uidFromMfaToken = (mfaToken: string): string | undefined => {
  try {
    const payload = mfaToken.split('.')[1];
    const { sub } = JSON.parse(
      new TextDecoder().decode(base64urlToBytes(payload))
    );
    return typeof sub === 'string' && /^[0-9a-f]{32}$/.test(sub)
      ? sub
      : undefined;
  } catch {
    return undefined;
  }
};
